import { Modal } from 'components'
import { ReactElement, ReactNode, useState } from 'react'
import { Stack, SxProps, Typography } from '@mui/material'
import { Theme, useTheme } from '@mui/material/styles'
import {
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  CheckCircle as SuccessIcon,
} from '@mui/icons-material'

export type AlertType = 'warning' | 'error' | 'info' | 'success'

export interface AlertModalProps {
  onConfirm?: () => void
  children?: ReactNode
  type?: AlertType
  title?: string
}

interface AlertTypeConfig {
  icon: ReactElement
  color: string
  title: string
}

const modalContainer: SxProps<Theme> = {
  flexDirection: 'row',
  alignItems: 'center',
  px: 3,
}

const modalDescription: SxProps<Theme> = {
  fontSize: 22,
  fontWeight: 'bold',
  maxWidth: 380,
  color: (theme: Theme) => theme.palette.text.secondary,
}

const iconStyle: SxProps<Theme> = {
  fontSize: 48,
  marginRight: 2,
}

const createAlertConfig = (
  theme: Theme
): Record<AlertType, AlertTypeConfig> => ({
  warning: {
    icon: (
      <WarningIcon sx={{ ...iconStyle, color: theme.palette.warning.main }} />
    ),
    color: theme.palette.warning.main,
    title: 'Warning',
  },
  error: {
    icon: <ErrorIcon sx={{ ...iconStyle, color: theme.palette.error.main }} />,
    color: theme.palette.error.main,
    title: 'Error',
  },
  info: {
    icon: <InfoIcon sx={{ ...iconStyle, color: theme.palette.info.main }} />,
    color: theme.palette.info.main,
    title: 'Info',
  },
  success: {
    icon: (
      <SuccessIcon sx={{ ...iconStyle, color: theme.palette.success.main }} />
    ),
    color: theme.palette.success.main,
    title: 'Success',
  },
})

export const useAlertModal = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [config, setConfig] = useState<AlertModalProps>({})
  const theme = useTheme()
  const alertConfig = createAlertConfig(theme)

  const showModal = (options: AlertModalProps) => {
    setConfig(options)
    setIsOpen(true)
  }

  const hideModal = () => {
    setIsOpen(false)
  }

  const handleConfirm = () => {
    if (config.onConfirm) {
      config.onConfirm()
    }
    hideModal()
  }

  const type = config.type || 'warning'
  const currentAlert = alertConfig[type]

  const modal = (
    <Modal
      isOpen={isOpen}
      onClose={hideModal}
      title={config.title || currentAlert.title}
      hideConfirmButton={true}
    >
      <Stack sx={modalContainer}>
        <Stack>{currentAlert.icon}</Stack>
        <Typography sx={modalDescription}>{config.children}</Typography>
      </Stack>
    </Modal>
  )

  return {
    modal,
    showModal,
    hideModal,
    showWarningModal: (options: Omit<AlertModalProps, 'type'>) =>
      showModal({ ...options, type: 'warning' }),
    showErrorModal: (options: Omit<AlertModalProps, 'type'>) =>
      showModal({ ...options, type: 'error' }),
    showInfoModal: (options: Omit<AlertModalProps, 'type'>) =>
      showModal({ ...options, type: 'info' }),
    showSuccessModal: (options: Omit<AlertModalProps, 'type'>) =>
      showModal({ ...options, type: 'success' }),
  }
}
